import React, { useCallback, useEffect, useState } from 'react';
import { Trash2 } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import ConfirmDialog from '../components/ConfirmDialog';
import { adminApi } from '../../services/api';
import { useToast } from '../hooks/useToast';

const emptyStore = {
  name: '',
  code: '',
  phone: '',
  email: '',
  managerName: '',
  openingHours: '',
  mapUrl: '',
  isActive: true,
  address: { fullAddress: '', city: '', state: '', pincode: '', landmark: '' }
};

const Stores = () => {
  const [stores, setStores] = useState([]);
  const [form, setForm] = useState(emptyStore);
  const [editingId, setEditingId] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { success, warning, error: showError } = useToast();

  const loadStores = useCallback(() => {
    adminApi.listStores()
      .then((response) => {
        setStores(response.data?.data || []);
        setSelectedIds([]);
      })
      .catch((error) => {
        setStores([]);
        warning(error.response?.data?.message || 'Could not load stores.');
      });
  }, [warning]);

  useEffect(() => {
    loadStores();
  }, [loadStores]);

  const setAddress = (key, value) => setForm((prev) => ({ ...prev, address: { ...prev.address, [key]: value } }));

  const saveStore = async (event) => {
    event.preventDefault();
    try {
      if (editingId) {
        await adminApi.updateStore(editingId, form);
        success('Store updated');
      } else {
        await adminApi.createStore(form);
        success('Store created');
      }
      setForm(emptyStore);
      setEditingId('');
      loadStores();
    } catch (error) {
      showError(error.response?.data?.message || 'Could not save store.');
    }
  };

  const editStore = (store) => {
    setEditingId(store.id);
    setForm(stores.find((item) => item._id === store.id) || emptyStore);
  };

  const resetForm = () => {
    setForm(emptyStore);
    setEditingId('');
  };

  const toggleSelection = (id) => {
    setSelectedIds((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
  };

  const allSelected = stores.length > 0 && stores.every((store) => selectedIds.includes(store._id));
  const toggleAll = () => setSelectedIds(allSelected ? [] : stores.map((store) => store._id));

  const deleteStores = async (ids) => {
    if (!ids.length) return;
    try {
      setDeleting(true);
      if (ids.length === 1) {
        await adminApi.deleteStore(ids[0]);
      } else {
        await adminApi.bulkDeleteStores(ids);
      }
      success(ids.length === 1 ? 'Store deleted' : `${ids.length} stores deleted`);
      setDeleteTarget(null);
      setBulkDeleteOpen(false);
      loadStores();
    } catch (error) {
      showError(error.response?.data?.message || 'Could not delete store.');
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    {
      key: 'select',
      label: (
        <input
          type="checkbox"
          checked={allSelected}
          onChange={toggleAll}
          aria-label="Select all stores"
          className="h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
        />
      ),
      sortable: false,
      render: (_, row) => (
        <input
          type="checkbox"
          checked={selectedIds.includes(row.id)}
          onChange={() => toggleSelection(row.id)}
          aria-label={`Select ${row.name}`}
          className="h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
        />
      )
    },
    {
      key: 'name',
      label: 'Store',
      sortable: true,
      render: (val, row) => (
        <div>
          <p className="font-medium text-gray-900">{val}</p>
          <p className="text-xs text-gray-500">{row.code || 'No code'} · {row.managerName || 'No manager'}</p>
        </div>
      )
    },
    { key: 'fullAddress', label: 'Full Address', sortable: true },
    { key: 'city', label: 'City', sortable: true },
    { key: 'pincode', label: 'Pincode', sortable: true },
    { key: 'phone', label: 'Phone' },
    { key: 'active', label: 'Status' }
  ];

  const rows = stores.map((store) => ({
    id: store._id,
    name: store.name,
    code: store.code,
    managerName: store.managerName,
    fullAddress: [store.address?.fullAddress, store.address?.landmark, store.address?.city, store.address?.state].filter(Boolean).join(', '),
    city: store.address?.city,
    pincode: store.address?.pincode,
    phone: store.phone,
    active: store.isActive ? 'Active' : 'Inactive'
  }));

  return (
    <div className="space-y-6">
      <PageHeader title="Stores" description="Manage pickup stores and complete addresses" breadcrumbs={['Home', 'Stores']} />

      <form onSubmit={saveStore} className="bg-white rounded-xl border border-gray-100 p-6 grid md:grid-cols-2 gap-4">
        <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Store name" className="border border-gray-200 rounded-lg px-4 py-2" />
        <input value={form.code || ''} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="Store code" className="border border-gray-200 rounded-lg px-4 py-2" />
        <input value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone" className="border border-gray-200 rounded-lg px-4 py-2" />
        <input value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="border border-gray-200 rounded-lg px-4 py-2" />
        <input value={form.managerName || ''} onChange={(e) => setForm({ ...form, managerName: e.target.value })} placeholder="Manager name" className="border border-gray-200 rounded-lg px-4 py-2" />
        <input value={form.openingHours || ''} onChange={(e) => setForm({ ...form, openingHours: e.target.value })} placeholder="Opening hours" className="border border-gray-200 rounded-lg px-4 py-2" />
        <input required value={form.address.fullAddress} onChange={(e) => setAddress('fullAddress', e.target.value)} placeholder="Full address" className="md:col-span-2 border border-gray-200 rounded-lg px-4 py-2" />
        <input required value={form.address.city} onChange={(e) => setAddress('city', e.target.value)} placeholder="City" className="border border-gray-200 rounded-lg px-4 py-2" />
        <input required value={form.address.state} onChange={(e) => setAddress('state', e.target.value)} placeholder="State" className="border border-gray-200 rounded-lg px-4 py-2" />
        <input required value={form.address.pincode} onChange={(e) => setAddress('pincode', e.target.value)} placeholder="Pincode" className="border border-gray-200 rounded-lg px-4 py-2" />
        <input value={form.address.landmark || ''} onChange={(e) => setAddress('landmark', e.target.value)} placeholder="Landmark" className="border border-gray-200 rounded-lg px-4 py-2" />
        <label className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} /> Active store</label>
        <div className="flex justify-end gap-3">
          {editingId && (
            <button type="button" onClick={resetForm} className="rounded-lg border border-gray-200 px-5 py-2 font-semibold text-gray-700 hover:bg-gray-50">
              Cancel Edit
            </button>
          )}
          <button className="bg-amber-600 text-white rounded-lg px-5 py-2 font-semibold">{editingId ? 'Update Store' : 'Create Store'}</button>
        </div>
      </form>

      <div className="flex flex-col gap-3 rounded-xl border border-gray-100 bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-gray-600">
          {selectedIds.length > 0 ? `${selectedIds.length} store${selectedIds.length === 1 ? '' : 's'} selected` : 'Select stores for bulk delete'}
        </p>
        <button
          type="button"
          disabled={selectedIds.length === 0}
          onClick={() => setBulkDeleteOpen(true)}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Trash2 size={16} />
          Delete Selected
        </button>
      </div>

      <DataTable columns={columns} data={rows} onEdit={editStore} onDelete={(id) => setDeleteTarget(stores.find((store) => store._id === id))} searchable paginate />

      <ConfirmDialog
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteStores([deleteTarget?._id].filter(Boolean))}
        title="Delete Store"
        message={`Delete "${deleteTarget?.name || 'this store'}"?`}
        confirmText={deleting ? 'Deleting...' : 'Delete'}
        isDangerous
      />

      <ConfirmDialog
        isOpen={bulkDeleteOpen}
        onClose={() => setBulkDeleteOpen(false)}
        onConfirm={() => deleteStores(selectedIds)}
        title="Bulk Delete Stores"
        message={`Delete ${selectedIds.length} selected store${selectedIds.length === 1 ? '' : 's'}?`}
        confirmText={deleting ? 'Deleting...' : 'Delete Selected'}
        isDangerous
      />
    </div>
  );
};

export default Stores;
